import { useSeoMeta } from '@unhead/vue';
import { computed, type MaybeRefOrGetter, toValue } from 'vue';

import { ProjectSettings } from '../context';
import { SeoRobots, type SeoDescriptor } from './types';

type SeoHeadContent = {
  description: string;
  robots: SeoRobots;
  title: string;
};

const translateSeoMessage = (
  message: SeoDescriptor['title'],
  translate: (key: string, params?: Record<string, number | string>) => string,
): string =>
  translate(message.key, {
    projectName: ProjectSettings.project.name,
    ...message.params,
  });

const formatSeoTitle = (title: string, productName: string): string =>
  title === '' ? productName : `${title} | ${productName}`;

const createTitle = (title?: string): string =>
  formatSeoTitle(title ?? '', ProjectSettings.project.name);

const useSeoHead = (
  descriptor: MaybeRefOrGetter<SeoDescriptor | null>,
  translate: (key: string, params?: Record<string, number | string>) => string,
): void => {
  const content = computed<SeoHeadContent | null>(() => {
    const value = toValue(descriptor);

    if (value === null) {
      return null;
    }

    return {
      description: translateSeoMessage(value.description, translate),
      robots: value.robots,
      title: translateSeoMessage(value.title, translate),
    };
  });

  useSeoMeta({
    description: () => content.value?.description ?? '',
    ogDescription: () => content.value?.description ?? '',
    ogTitle: () => formatSeoTitle(content.value?.title ?? '', ProjectSettings.project.name),
    ogType: 'website',
    ogSiteName: ProjectSettings.project.name,
    robots: () => content.value?.robots ?? SeoRobots.NOINDEX_NOFOLLOW,
    title: () => content.value?.title ?? ProjectSettings.project.name,
    titleTemplate: createTitle,
    twitterCard: 'summary',
    twitterDescription: () => content.value?.description ?? '',
    twitterTitle: () => formatSeoTitle(content.value?.title ?? '', ProjectSettings.project.name),
  });
};

export { formatSeoTitle, useSeoHead };
